import { useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { DateTime } from "luxon";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { NewProfileApplicationFrame } from "./NewProfileApplicationFrame";
import { Box } from "../../../components/Box";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Input } from "../../../components/Input";
import Select, { Option } from "../../../components/Select";
import { DatePicker } from "../../../components/DatePicker";
import { Modal } from "../../../components/Modal";
import { Loader } from "../../../components/Loader";
import { ROUTE } from "../../../routes";
import { useCountries } from "../../../data/useCountries";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { usePatchProfile } from "../../../data/usePatchProfile";
import { useCommonTitles } from "../../../data/useCommonTitles";
import { showError } from "../../../utils/error-handling";
import {
  getAddIndividualValidation,
  noHtmlTags,
} from "../../../utils/validationSchema";
import { MIN_AGE_APPLICATION } from "../../../utils/constants";
import { useCreateComment } from "../../../data/useCreateComment";
import { notify } from "../../../utils/notify";
import { useCompanyStatusOptions } from "../../../data/fallbackPayloads/useCompanyStatusOptions";

type EntityFormValues = {
  // entity
  name: string;
  country: number | null;
  entity_type: number | null;
  company_status: number | null;
  registration_number: string;
  date_of_incorporation: Date | null;

  // individual
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: Date | null;
  email: string;
  gender: string;
};

const rowProps: any = {
  flexWrap: "wrap",
  gap: "16",
  my: "4",
};

const inputProps = {
  minWidth: 200,
  flex: 1,
};

const genderOptions: Option[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

const normalize = (v: any) => {
  if (v === undefined || v === null) return "";
  if (v instanceof Date) return DateTime.fromJSDate(v).toISODate() ?? "";
  if (typeof v === "number") return String(v);
  return String(v).trim();
};

const hasChanges = (a: Record<string, any>, b: Record<string, any>) => {
  const keys = Object.keys(a);
  return keys.some((k) => normalize(a[k]) !== normalize(b[k]));
};

const toJSDate = (value?: string | null) => {
  if (!value) return null;
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toJSDate() : null;
};

export const EntityDetailsStep = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  const { profile } = useOutletContext<any>();

  const { data: countries, isLoading: isLoadingCountries } =
    useCountries() as any;
  const { data: entityTypes, isLoading: isLoadingEntityTypes } =
    useEntityTypes() as any;
  const { data: titlesData, isLoading: isLoadingTitles } =
    useCommonTitles() as any;

  const { mutate: patchProfile } = usePatchProfile();
  const { mutate: createComment } = useCreateComment();

  const isEntity = profile?._type === "entity";

  const countryOptions: Option[] = useMemo(() => {
    const list = Array.isArray(countries) ? countries : countries?.data ?? [];
    return list.map((c: any) => ({ label: c.name, value: c.id }));
  }, [countries]);

  const entityTypeOptions: Option[] = useMemo(() => {
    const list = Array.isArray(entityTypes)
      ? entityTypes
      : entityTypes?.data ?? [];
    return list.map((t: any) => ({ label: t.name, value: t.id }));
  }, [entityTypes]);

  const titleOptions: Option[] = useMemo(() => {
    return (titlesData?.data ?? []).map(({ name }: any) => ({
      label: name,
      value: name,
    }));
  }, [titlesData]);

  const { options: companyStatusOptions, isLoading: isLoadingCompanyStatuses } =
    useCompanyStatusOptions();

  const toId = (v: any): number | null => {
    if (typeof v === "number") return v;
    if (v && typeof v === "object" && typeof v.id === "number") return v.id;
    return null;
  };

  // ✅ ensure numeric selects are either number or null (never "")
  const initialValues: EntityFormValues = useMemo(
    () => ({
      // entity
      name: profile?.name ?? "",
      country: toId(profile?.country),
      entity_type: toId(profile?.entity_type),
      company_status:
        typeof profile?.company_status === "number"
          ? profile.company_status
          : typeof profile?.company_status?.id === "number"
          ? profile.company_status.id
          : null,
      registration_number: profile?.registration_number ?? "",
      date_of_incorporation: toJSDate(profile?.date_of_incorporation),

      // individual
      title: profile?.title ?? null,
      firstName: profile?.first_name ?? "",
      middleName: profile?.middle_name ?? "",
      lastName: profile?.last_name ?? "",
      dateOfBirth: toJSDate(profile?.date_of_birth),
      email: profile?.email ?? "",
      gender: profile?.gender ?? null,
    }),
    [profile],
  );

  // ✅ transform "" => null so optional numbers don’t block validation
  const entityValidation = useMemo(() => {
    const Yup = require("yup");

    const nullableNumber = () =>
      Yup.number()
        .nullable()
        .transform((value: any, originalValue: any) =>
          originalValue === "" || originalValue === undefined ? null : value,
        );

    return Yup.object().shape({
      name: noHtmlTags(Yup.string().trim()).required("Entity name is required"),
      country: nullableNumber(), // optional
      entity_type: nullableNumber().required("Entity type is required"),
      company_status: nullableNumber(),
      registration_number: noHtmlTags(Yup.string().trim()).nullable(),
      date_of_incorporation: Yup.mixed().nullable(),
    });
  }, []);

  const validationSchema = useMemo(() => {
    return isEntity
      ? entityValidation
      : getAddIndividualValidation({
          requireEmail: true,
          requireDivisionTeam: false,
        });
  }, [isEntity, entityValidation]);

  const isLoadingLookups =
    isLoadingCountries ||
    isLoadingEntityTypes ||
    isLoadingTitles ||
    isLoadingCompanyStatuses;

  const [pendingValues, setPendingValues] = useState<EntityFormValues | null>(
    null,
  );
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);

  const goNext = (values: EntityFormValues) => {
    navigate(
      ROUTE.PROFILE_APPLICATION_APPLICATION_TYPE.replace(
        ":id",
        profileId.toString(),
      ),
      {
        state: {
          detailsOverride: values,
        },
      },
    );
  };

  const comparableSubset = (values: EntityFormValues) => {
    if (isEntity) {
      return {
        name: values.name,
        country: values.country,
        entity_type: values.entity_type,
        company_status: values.company_status,
        registration_number: values.registration_number,
        date_of_incorporation: values.date_of_incorporation,
      };
    }
    return {
      title: values.title,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      email: values.email,
      gender: values.gender,
    };
  };

  const handleNext = (values: EntityFormValues) => {
    const changed = hasChanges(
      comparableSubset(values),
      comparableSubset(initialValues),
    );

    if (!changed) {
      goNext(values);
      return;
    }

    setPendingValues(values);
    setIsChangeModalOpen(true);
  };

  const handleSkip = () => {
    setIsChangeModalOpen(false);
    if (pendingValues) goNext(pendingValues);
  };

  const handleUpdate = (comment: string) => {
    if (!pendingValues) {
      setIsChangeModalOpen(false);
      return;
    }

    if (!comment?.trim()) {
      showError(null as any, "Please enter a comment before updating.");
      return;
    }

    const patchPayload = isEntity
      ? {
          _type: "entity" as const,
          name: pendingValues.name,
          country: pendingValues.country,
          entity_type: pendingValues.entity_type,
          company_status: pendingValues.company_status || null,
          registration_number: pendingValues.registration_number || null,
          date_of_incorporation: pendingValues.date_of_incorporation
            ? DateTime.fromJSDate(
                pendingValues.date_of_incorporation,
              ).toISODate()
            : null,
        }
      : {
          _type: "individual" as const,
          title: pendingValues.title || null,
          gender: pendingValues.gender || null,
          first_name: pendingValues.firstName,
          middle_name: pendingValues.middleName || null,
          last_name: pendingValues.lastName,
          date_of_birth: pendingValues.dateOfBirth
            ? DateTime.fromJSDate(pendingValues.dateOfBirth as Date).toISODate()
            : null,
          email: pendingValues.email || null,
        };

    patchProfile({ profileId, ...patchPayload } as any, {
      onSuccess: () => {
        notify("Profile details updated successfully!", {
          position: "bottom-left",
          type: "success",
        });

        createComment(
          {
            comment: comment.trim(),
            comment_for: "Profile",
            reference: profileId,
          },
          {
            onSuccess: () => {
              setIsChangeModalOpen(false);
              goNext(pendingValues);
            },
            onError: (err: any) => {
              showError(err, "Profile updated but failed to add comment.");
            },
          },
        );
      },
      onError: (err: any) => {
        showError(err, "Failed to update Profile.");
      },
    });
  };

  return (
    <NewProfileApplicationFrame step="entityDetails">
      <Box mt={2}>
        <H2>{isEntity ? "Entity Details" : "Individual Details"}</H2>
        <Paragraph>
          These details will be used to create the application. You can edit
          them here before continuing.
        </Paragraph>

        {isLoadingLookups && (
          <Flex justifyContent="center" alignItems="center" minHeight="25vh">
            <Loader />
          </Flex>
        )}

        {!isLoadingLookups && (
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validateOnMount
            validationSchema={validationSchema}
            onSubmit={handleNext}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit} style={{ width: "510px" }}>
                {isEntity ? (
                  <>
                    <Flex {...rowProps}>
                      <Input
                        {...inputProps}
                        name="name"
                        label="Entity name"
                        placeholder="Enter entity name"
                        mt="2"
                        value={props.values.name}
                        onChange={props.handleChange}
                        hasError={Boolean(
                          props.touched.name && (props.errors as any).name,
                        )}
                        errorMessage={(props.errors as any).name as string}
                        isRequired
                      />
                    </Flex>

                    <Flex {...rowProps}>
                      <div style={{ width: "100%" }}>
                        <Select
                          name="country"
                          label="Country of registration/establishment"
                          placeholder="Select country"
                          mt="2"
                          flex={1}
                          options={countryOptions}
                          value={countryOptions.find(
                            (o) => o.value === props.values.country,
                          )}
                          onChange={(option: any) =>
                            props.setFieldValue(
                              "country",
                              option?.value ?? null,
                            )
                          }
                        />
                      </div>
                    </Flex>

                    <Flex {...rowProps}>
                      <div style={{ width: "48%" }}>
                        <Select
                          name="entity_type"
                          label="Entity type"
                          placeholder="Select entity type"
                          mt="2"
                          isRequired
                          flex={1}
                          options={entityTypeOptions}
                          value={entityTypeOptions.find(
                            (o) => o.value === props.values.entity_type,
                          )}
                          onChange={(option: any) =>
                            props.setFieldValue(
                              "entity_type",
                              option?.value ?? null,
                            )
                          }
                        />
                      </div>

                      <div style={{ width: "48%" }}>
                        <Select
                          name="company_status"
                          label="Current status"
                          placeholder="Select current status"
                          mt="2"
                          flex={1}
                          options={companyStatusOptions}
                          value={companyStatusOptions.find(
                            (o) => o.value === props.values.company_status,
                          )}
                          onChange={(option: any) =>
                            props.setFieldValue(
                              "company_status",
                              option?.value ?? null,
                            )
                          }
                        />
                      </div>
                    </Flex>

                    <Flex {...rowProps}>
                      <Input
                        {...inputProps}
                        name="registration_number"
                        label="Registration number"
                        placeholder="Enter registration number"
                        mt="2"
                        value={props.values.registration_number}
                        onChange={props.handleChange}
                      />

                      <DatePicker
                        {...inputProps}
                        name="date_of_incorporation"
                        label="Date of registration/establishment"
                        mt="2"
                        value={props.values.date_of_incorporation as any}
                        onChange={(date: any) =>
                          props.setFieldValue("date_of_incorporation", date)
                        }
                      />
                    </Flex>
                  </>
                ) : (
                  <>
                    <Flex {...rowProps}>
                      <div style={{ width: "48%" }}>
                        <Select
                          name="title"
                          label="Title"
                          placeholder="Select title"
                          mt="2"
                          flex={1}
                          options={titleOptions}
                          value={titleOptions.find(
                            (o) => o.value === props.values.title,
                          )}
                          onChange={(option: any) =>
                            props.setFieldValue("title", option?.value ?? null)
                          }
                        />
                      </div>

                      <div style={{ width: "48%" }}>
                        <Select
                          name="gender"
                          label="Select gender"
                          placeholder="Select gender"
                          mt="2"
                          isRequired
                          flex={1}
                          options={genderOptions}
                          value={genderOptions.find(
                            (o) => o.value === props.values.gender,
                          )}
                          onChange={(option: any) =>
                            props.setFieldValue("gender", option?.value ?? "")
                          }
                          hasError={Boolean(
                            props.touched.gender &&
                              (props.errors as any).gender,
                          )}
                          errorMessage={(props.errors as any).gender as string}
                        />
                      </div>
                    </Flex>

                    <Flex {...rowProps}>
                      <Input
                        {...inputProps}
                        name="firstName"
                        label="First name"
                        placeholder="Enter first name"
                        mt="2"
                        value={props.values.firstName}
                        onChange={props.handleChange}
                        hasError={Boolean(
                          props.touched.firstName &&
                            (props.errors as any).firstName,
                        )}
                        errorMessage={(props.errors as any).firstName as string}
                        isRequired
                      />
                      <Input
                        {...inputProps}
                        name="middleName"
                        label="Middle name(s)"
                        placeholder="Enter middle(s) name"
                        mt="2"
                        value={props.values.middleName}
                        onChange={props.handleChange}
                        hasError={Boolean(
                          props.touched.middleName &&
                            (props.errors as any).middleName,
                        )}
                        errorMessage={
                          (props.errors as any).middleName as string
                        }
                      />
                    </Flex>

                    <Flex {...rowProps}>
                      <Input
                        {...inputProps}
                        name="lastName"
                        label="Last name"
                        placeholder="Enter last name"
                        mt="2"
                        value={props.values.lastName}
                        onChange={props.handleChange}
                        hasError={Boolean(
                          props.touched.lastName &&
                            (props.errors as any).lastName,
                        )}
                        errorMessage={(props.errors as any).lastName as string}
                        isRequired
                      />
                      <DatePicker
                        {...inputProps}
                        name="dateOfBirth"
                        label="Date of birth"
                        mt="2"
                        value={props.values.dateOfBirth as any}
                        onChange={(date: any) =>
                          props.setFieldValue("dateOfBirth", date)
                        }
                        maxDate={DateTime.now()
                          .minus({ years: MIN_AGE_APPLICATION })
                          .toJSDate()}
                        hasError={Boolean(
                          props.touched.dateOfBirth &&
                            (props.errors as any).dateOfBirth,
                        )}
                        errorMessage={
                          (props.errors as any).dateOfBirth as string
                        }
                      />
                    </Flex>

                    <Flex {...rowProps}>
                      <Input
                        {...inputProps}
                        name="email"
                        label="Email address"
                        placeholder="Enter email address"
                        mt="2"
                        value={props.values.email}
                        onChange={props.handleChange}
                        hasError={Boolean(
                          props.touched.email && (props.errors as any).email,
                        )}
                        errorMessage={(props.errors as any).email as string}
                        isRequired
                      />
                    </Flex>
                  </>
                )}

                <Flex justifyContent="flex-end" mt={4}>
                  <Button
                    type="submit"
                    isDisabled={!props.isValid || props.isSubmitting}
                  >
                    Next
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        )}
      </Box>

      <Modal
        isOpen={isChangeModalOpen}
        onClose={() => setIsChangeModalOpen(false)}
        title="Update profile?"
        content="Would you update the profile record with the changes you have made. If so please enter a comment before updating."
        hasSecondaryActionButton
        secondaryButtonText="Skip"
        secondaryButtonVariant="text"
        onSecondaryButtonClick={handleSkip}
        width="520px"
        primaryButtonText="Update"
        primaryButtonVariant="primary"
        onModalConfirmClick={handleUpdate}
        hasCommentBox
        loadingActionText="Updating..."
      />
    </NewProfileApplicationFrame>
  );
};
